import styled from 'styled-components';
import ShowroomModal from '@carvana/showroom-modal';
import { CarvanaTheme } from '@carvana/showroom-theme';
import { media } from '@carvana/stc-styles';
import { Radio, RadioGroup } from '../components/RadioGroup';

const ButtonBase = styled.button<{ disabled?: boolean }>`
  appearance: none;
  align-items: center;
  background-color: ${({ disabled }) => (disabled ? 'rgba(0, 0, 0, 0.20)' : 'transparent')};
  border-radius: 2px;
  border: 0;
  box-sizing: border-box;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  display: inline-flex;
  font-family: 'Brandon Text';
  font-size: 14px;
  font-weight: bold;
  justify-content: center;
  line-height: 1.4em;
  margin: 0;
  min-height: 36px;
  min-width: 88px;
  padding: 0;
  pointer-events: ${props => props.disabled && 'none'};
  position: relative;
  -webkit-tap-highlight-color: transparent;
  text-decoration: none;
  text-transform: uppercase;
  transition:
    background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  user-select: none;
  vertical-align: middle;
`;

export const CtaButton = styled(ButtonBase)`
  font-weight: 600;
  padding: 16px;
  background: ${({ theme }) => theme.color.primary.v200};
  font-size: 14px;
  color: #ffffff;
  min-width: 180px;
  height: 56px;
  border-radius: 3px;

  &:hover {
    ${media.sm`
        background: ${({ theme }: { theme: typeof CarvanaTheme }) => theme.color.primary.v300};
      `}
  }

  &[disabled] {
    background: ${({ theme }) => theme.color.neutral.v300};
    color: ${({ theme }) => theme.color.neutral.v400};
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.07);
    border: none;
  }
`;

export const CtaButtonOutline = styled(CtaButton)`
  background: ${({ theme }) => theme.color.neutral.v100};
  color: ${({ theme }) => theme.color.primary.v200};
  border: 1px solid ${({ theme }) => theme.color.primary.v100};
  border-radius: 3px;

  &:hover {
    ${media.sm`
      border: 1px solid ${({ theme }: { theme: typeof CarvanaTheme }) => theme.color.primary.v300};
      color: ${({ theme }: { theme: typeof CarvanaTheme }) => theme.color.neutral.v100};
    `}
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 24px 24px 24px;

  & > form {
    width: 100%;
  }
`;

export const ActionButton = styled(CtaButton)`
  && {
    margin: unset;
    min-width: unset;
    width: 100%;
    margin-top: 24px;
    margin-left: 8px;
  }
`;

export const SkipButton = styled(CtaButtonOutline)`
  && {
    margin: unset;
    min-width: unset;
    width: 100%;
    margin-top: 24px;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 24px;
`;

export const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const StyledRadio = styled(Radio)`
  font-size: 36px;
  cursor: pointer;
  width: 56px;
  outline: none;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:focus {
    outline: none;
  }

  & > div {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 56px;
    width: 56px;
    padding: 0;
  }

  & > p {
    padding-bottom: 12px;
  }
`;

export const Label = styled.div`
  color: ${({ theme }) => theme.color.primary.v600};
  font-weight: 550;
  font-size: 20px;
  line-height: 24px;
  margin-bottom: 16px;
`;

export const StyledHeader = styled(Label)`
  text-transform: none;
  margin-bottom: 24px;
  max-width: 400px;

  .modal--header {
    color: ${({ theme }) => theme.color.primary.v600} !important;
  }
`;

export const Item = styled.div`
  border: 1px solid transparent;
  padding: 8px;
  outline: none;
  background: #ffffff;
  border-radius: 8px;

  &.selected {
    border-color: ${({ theme }) => theme.color.primary.v200};
    background: ${({ theme }) => theme.color.primary.v200};
  }
`;

export const ItemDesc = styled.p`
  opacity: 0;
  outline: none;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  margin-top: 8px;
  text-transform: capitalize;
  color: ${({ theme }) => theme.color.primary.v600};
  font-weight: 700;

  &.selected {
    opacity: 1;
    margin-left: -100%;
    margin-right: -100%;
  }
`;

export const LeadIn = styled.p`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: ${({ theme }) => theme.color.neutral.v500};
  margin-bottom: 24px;
  margin-top: 12px;
  text-align: center;
`;

export const HorizontalRule = styled.div`
  height: 1px;
  width: 100%;
  background: rgba(0, 0, 0, 0.12);
  margin-bottom: 24px;
`;

export const GetSupport = styled.div`
  ${({ theme }) => theme.font.label.v100};
  color: ${({ theme }) => theme.color.neutral.v500};
`;

export const ThankYouContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 586px;
  justify-content: center;
`;

export const ThankYouEmoji = styled.div`
  font-size: 96px;
  margin-bottom: 32px;
`;

export const Background = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.v200};
  width: 100%;
`;

export const Container = styled.div``;

export const StyledTextArea = styled.textarea`
  display: flex;
  border: 1px solid ${({ theme }) => theme.color.neutral.v300};
  border-radius: 3px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.color.primary.v600};
  font-family: ${({ theme }) => theme.font.family};
  font-weight: 100;
  font-size: 16px;
  height: 100px;
  overflow: hidden;
  margin-bottom: 8px;
  padding: 10px;
  resize: none;
  width: 100%;
  -webkit-appearance: none;
  ::placeholder {
    color: ${({ theme }) => theme.color.neutral.v400};
  }
`;

export const TestWrapper = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Modal = styled(ShowroomModal)`
  .modal--scroll-area {
    margin: 0;
  }
  & > .modal--content {
    background: ${({ theme }) => theme.color.neutral.v200};
  }

  & > .header--container {
    background: ${({ theme }) => theme.color.neutral.v100};
    box-shadow: ${({ theme }) => theme.depth.elevation.v200};

    & > .modal--header {
      color: ${({ theme }) => theme.color.primary.v600};
    }
  }
`;

export const ModalThatHidesChatButton = styled(Modal)`
  font-family: 'Brandon Text' !important;

  .modal--content {
    ${media.sm`
      max-width: 487px;
    `}
  }
  z-index: 2001;

  & a {
    color: #007bff;
    text-decoration: none;
    background-color: transparent;
    -webkit-text-decoration-skip: objects;
  }
`;
